<template>
  <modal :show.sync="internalShow" @close="handleClose" size="lg">
    <template #header>
      <div class="text-xl font-semibold">Payment Details</div>
    </template>
    <div class="payment-popup">
      <!-- Sale Summary -->
      <div
        class="p-4 bg-gray-100 border-l-4 border-blue-500 rounded-lg sale-summary"
      >
        <h4 class="mb-2 font-bold">Sale Summary</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-5">
          <div class="flex flex-col">
            <strong>Total:</strong>
            <span class="text-blue-600">{{ rawTotal | currency }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Discount:</strong>
            <span class="text-yellow-600">{{ discountAmount | currency }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Round Off:</strong>
            <span class="text-gray-600">{{ roundOff | currency }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Amount Due:</strong>
            <span class="text-green-600">{{ amountDue | currency }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Customer:</strong>
            <span>{{ customerName }}</span>
          </div>
        </div>
      </div>

      <!-- Payment Modes -->
      <div class="mt-2 payment-modes">
        <h4 class="mb-2 font-bold">Payment Details</h4>
        <div class="flex flex-row justify-between gap-2">
          <div
            v-for="paymentMode in sortedPaymentModes"
            :key="paymentMode.paymentModeId"
            class="flex flex-col p-2 justify-between min-w-[300px] bg-white rounded-lg border"
          >
            <div class="w-1/3 font-medium">
              <a @click="handlePaymentModeClick(paymentMode)">{{
                paymentMode.label
              }}</a>
            </div>
            <div class="w-full">
              <input
                :value="paymentAmounts[paymentMode.paymentModeId] || 0"
                @input="
                  updatePaymentAmount(
                    paymentMode.paymentModeId,
                    $event.target.value
                  )
                "
                type="number"
                min="0"
                class="w-full border border-gray-300 form-input"
                :placeholder="`${paymentMode.label} amount`"
              />
            </div>
          </div>
        </div>

        <!-- Total Paid and Balance -->
        <div class="p-4 mt-2 bg-gray-100 rounded-lg payment-summary">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <strong>Paying: </strong>
              <span class="text-green-600">{{ totalPaid | currency }}</span>
            </div>
            <div>
              <strong>Balance: </strong>
              <span :class="balanceClass">{{ balance | currency }}</span>
            </div>
          </div>
          <div
            v-if="balance > 0"
            class="p-2 mt-2 text-yellow-800 bg-yellow-100 border border-yellow-400 rounded"
          >
            <i class="mr-1 fas fa-exclamation-triangle"></i>
            <strong>Note:</strong> There is a pending balance of
            {{ balance | currency }}. The customer can pay this amount later.
          </div>
        </div>

        <!-- Payment Note -->
        <div class="mt-4 payment-note">
          <label class="block mb-1 font-medium">Payment Note</label>
          <textarea
            v-model="paymentNote"
            class="w-full border border-gray-300 rounded form-textarea"
            rows="3"
            placeholder="Enter any additional payment notes..."
          ></textarea>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end space-x-2">
        <button
          @click="handleClose"
          class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="handlePrint"
          class="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600"
        >
          Print Receipt
        </button>
        <button
          type="button"
          :disabled="saving"
          @click="handleSave"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
        >
          <span v-if="saving" class="mr-2 fas fa-spinner fa-spin"></span>
          Save Sale
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/Modal.vue";

export default {
  name: "PaymentPopup",
  components: { Modal },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    totalAmount: {
      type: Number,
      default: 0
    },
    rawTotal: {
      type: Number,
      default: 0
    },
    discountMode: {
      type: String,
      default: "percent"
    },
    discount: {
      type: Number,
      default: 0
    },
    roundOff: {
      type: Number,
      default: 0
    },
    amountDue: {
      type: Number,
      default: 0
    },
    customerName: {
      type: String,
      default: ""
    },
    selectedProducts: {
      type: Array,
      default: () => []
    },
    saleData: {
      type: Object,
      default: () => ({})
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      paymentAmounts: {},
      paymentNote: "",
      internalShow: this.show
    };
  },
  computed: {
    ...mapGetters({
      paymentModeList: "paymentMode/getPaymentModes"
    }),
    totalPaid() {
      return Object.values(this.paymentAmounts).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    balance() {
      return this.amountDue - this.totalPaid;
    },
    balanceClass() {
      if (this.balance === 0) {
        return "text-green-600 font-bold";
      } else if (this.balance > 0) {
        return "text-orange-600 font-bold";
      } else {
        return "text-red-600 font-bold";
      }
    },
    isValidPayment() {
      return this.totalPaid >= 0;
    },
    discountAmount() {
      if (this.discountMode === "percent") {
        return this.rawTotal * (this.discount / 100);
      } else {
        return this.discount;
      }
    },
    sortedPaymentModes() {
      const order = ["UPI", "Cash", "Card", "Cheque", "Online"];
      return [...this.paymentModeList].sort((a, b) => {
        const aIndex = order.indexOf(a.label);
        const bIndex = order.indexOf(b.label);

        // If both are in the order array, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only one is in the order array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // If neither is in the order array, sort alphabetically
        return a.label.localeCompare(b.label);
      });
    }
  },
  watch: {
    show(newVal) {
      this.internalShow = newVal;
      if (newVal) {
        this.initializePaymentAmounts();
      }
    },
    internalShow(newVal) {
      if (!newVal) {
        this.$emit("update:show", false);
      }
    }
  },
  methods: {
    initializePaymentAmounts() {
      this.paymentAmounts = {};
      this.paymentNote = "";
      // Initialize all payment modes with 0
      this.paymentModeList.forEach(mode => {
        this.$set(this.paymentAmounts, mode.paymentModeId, 0);
      });
    },
    updatePaymentAmount(modeId, value) {
      this.$set(this.paymentAmounts, modeId, parseFloat(value) || 0);
    },
    handleClose() {
      this.internalShow = false;
    },
    handlePrint() {
      this.$emit("print", this.getPaymentData());
    },
    handleSave() {
      this.$emit("save", this.getPaymentData());
    },
    handlePaymentModeClick(paymentMode) {
      this.paymentAmounts[paymentMode.paymentModeId] = this.amountDue;
    },
    getPaymentData() {
      const paymentBreakdown = Object.keys(this.paymentAmounts)
        .filter(modeId => this.paymentAmounts[modeId] > 0)
        .map(modeId => {
          const mode = this.paymentModeList.find(
            m => m.paymentModeId === modeId
          );
          return {
            paymentModeId: modeId,
            paymentModeLabel: mode ? mode.label : "",
            amount: parseFloat(this.paymentAmounts[modeId])
          };
        });

      return {
        totalPaid: this.totalPaid,
        balance: this.balance,
        paymentNote: this.paymentNote,
        paymentBreakdown,
        saleData: this.saleData,
        selectedProducts: this.selectedProducts
      };
    }
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
    }
  }
};
</script>

<style scoped>
.payment-popup {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
